// Games: who plays when, on Game Day (DIRECTION U22, B-318). Both sides'
// starters by the pro game they are in, the games by kickoff — Thursday
// night, Sunday's windows, the night games — each game with its state or its
// score, my players on the left of it and theirs on the right. A glance says
// which game to put on and how much of the week is still to come; the starters
// with no game this week (a bye, an empty slot) are listed last, so the count
// adds up.
//
// Facts only: who is in which game and what they have scored, or are
// projected to from Standard (DETAIL_LEVELS §Matchup). Nothing here says who
// should have started.

import type { GameStatus, MatchupRowView, MatchupSide } from '@fsp/domain';
import { whenInZone } from '@fsp/domain';
import { useReaderZone } from '../../data/hooks';
import { useLeagueNow } from '../../data/demo';
import { useAtLeast } from '../../data/detail';
import { EmptyState, PlayerMarks, PositionPill, ProTeamCode, TeamMark } from '../../kit';
import { Caption } from '../../kit/ui';
import { num } from '../shared/notComputed';
import '../../styles/screens/game-day.css';
import { clubLabel } from '../../sports/registry';
import { useTools } from '../../data/tools';

interface SlateGame {
  key: string;
  /** "SF at WAS": the away club first, as a schedule writes it. */
  title: string;
  game: GameStatus;
  mine: MatchupRowView[];
  theirs: MatchupRowView[];
}

interface SlateWindow {
  key: string;
  startAt: string;
  games: SlateGame[];
}

type Phase = 'pre' | 'live' | 'final';

/** Has a game this week that has a kickoff: the rows the slate places. */
function placed(
  r: MatchupRowView,
): r is MatchupRowView & { game: GameStatus & { startAt: string } } {
  return (
    r.player !== null &&
    r.game !== null &&
    r.game.startAt !== null &&
    (r.game.state === 'pre' || r.game.state === 'live' || r.game.state === 'final')
  );
}

/** The week's games with a starter of either side in them, by kickoff. */
export function slateOf(
  me: MatchupSide,
  them: MatchupSide,
): {
  windows: SlateWindow[];
  idle: { row: MatchupRowView; mine: boolean }[];
} {
  const games = new Map<string, SlateGame>();
  const idle: { row: MatchupRowView; mine: boolean }[] = [];
  const add = (rows: readonly MatchupRowView[], mine: boolean) => {
    for (const r of rows) {
      if (!placed(r)) {
        idle.push({ row: r, mine });
        continue;
      }
      const club = r.player!.proTeam ?? '';
      const other = r.game.opponent ?? '';
      const key = `${r.game.startAt}|${[club, other].sort().join('|')}`;
      let g = games.get(key);
      if (g === undefined) {
        g = {
          key,
          title: r.game.home
            ? `${clubLabel(other)} at ${clubLabel(club)}`
            : `${clubLabel(club)} at ${clubLabel(other)}`,
          game: r.game,
          mine: [],
          theirs: [],
        };
        games.set(key, g);
      }
      (mine ? g.mine : g.theirs).push(r);
    }
  };
  add(me.rows, true);
  add(them.rows, false);
  const windows = new Map<string, SlateWindow>();
  for (const g of games.values()) {
    const at = g.game.startAt!;
    const w = windows.get(at) ?? { key: at, startAt: at, games: [] };
    w.games.push(g);
    windows.set(at, w);
  }
  return {
    windows: [...windows.values()]
      .sort((a, b) => Date.parse(a.startAt) - Date.parse(b.startAt))
      .map((w) => ({ ...w, games: w.games.sort((a, b) => a.title.localeCompare(b.title)) })),
    idle,
  };
}

/**
 * A game's own line beside its title, as `GameLabel` words it (B-171): the
 * score and the clock while a running score exists, the score and Final once
 * it is over, and nothing before kickoff or while it has kicked off without a
 * score, which the band already says.
 */
function gameState(game: GameStatus): string | null {
  if (game.state === 'final') return game.score === null ? 'Final' : `${game.score} · Final`;
  if (game.state === 'live' && game.score !== null) return `${game.score} · ${game.label}`;
  return null;
}

/**
 * A short name with a place to break after its initial: in a phone's narrow
 * column "P.Washington" goes to two lines as "P." and "Washington" rather
 * than mid-word (walked at 390).
 */
function breakable(name: string) {
  const at = name.indexOf('.');
  if (at < 0 || at === name.length - 1) return name;
  return (
    <>
      {name.slice(0, at + 1)}
      <wbr />
      {name.slice(at + 1)}
    </>
  );
}

/** A window is final once every game in it is, kicked off once any is. */
function phaseOf(w: SlateWindow): Phase {
  if (w.games.every((g) => g.game.state === 'final')) return 'final';
  if (w.games.some((g) => g.game.state === 'live' || g.game.state === 'final')) return 'live';
  return 'pre';
}

export function GameSlate({
  me,
  them,
  onOpen,
}: {
  me: MatchupSide;
  them: MatchupSide;
  /** A player's name opens his card. */
  onOpen: (row: MatchupRowView) => void;
}) {
  const zone = useReaderZone();
  const now = useLeagueNow();
  // How many of each side a kickoff holds is Standard's number; Simple keeps
  // the points, which are the answer, and drops the rest (DETAIL_LEVELS).
  const counts = useAtLeast('standard');
  const { windows, idle } = slateOf(me, them);
  if (windows.length === 0 && idle.length === 0) {
    return <EmptyState>No games this week.</EmptyState>;
  }
  const meName = me.team.isMine ? 'You' : me.team.abbreviation;
  return (
    <div className="fsp-slate">
      {/* WHOSE SIDE IS WHICH, by mark and name, held at the top while the games
          scroll under it: "YOU" and "LL" once, at the head of a long list,
          left a reader halfway down guessing which numbers were whose. */}
      <div className="fsp-slate-sides" aria-hidden>
        <span className="fsp-slate-who">
          <TeamMark team={me.team} size={20} />
          <span className="fsp-slate-who-name">{me.team.isMine ? 'You' : me.team.name}</span>
        </span>
        <span className="fsp-slate-who" data-right>
          <TeamMark team={them.team} size={20} />
          <span className="fsp-slate-who-name">{them.team.name}</span>
        </span>
      </div>
      {windows.map((w) => {
        const phase = phaseOf(w);
        const mine = w.games.reduce((n, g) => n + g.mine.length, 0);
        const theirs = w.games.reduce((n, g) => n + g.theirs.length, 0);
        return (
          <section
            key={w.key}
            className="fsp-slate-window"
            data-phase={phase}
            aria-label={zone === null ? 'Kickoff' : whenInZone(w.startAt, zone, now)}
          >
            <div className="fsp-slate-when">
              <span className="fsp-slate-time">
                {zone === null ? w.games[0]!.game.label : whenInZone(w.startAt, zone, now)}
              </span>
              <span className="fsp-slate-phase">
                {/* KICKED OFF, NOT LIVE OR UNDER WAY (B-171): without a live feed a
                    game is called final in the morning's pull, so a window
                    whose games began says only that they did. */}
                {phase === 'final' ? 'Final' : phase === 'live' ? 'Kicked off' : 'Upcoming'}
              </span>
              {counts ? (
                <span className="fsp-sub fsp-slate-count">
                  {meName} {mine} · {them.team.abbreviation} {theirs}
                </span>
              ) : null}
            </div>
            {w.games.map((g) => (
              <div key={g.key} className="fsp-slate-game">
                <div className="fsp-slate-game-head">
                  <span className="fsp-slate-title">{g.title}</span>
                  {/* No live dot: the numbers arrive overnight, and the band
                      already says the game is under way (B-171). */}
                  <span className="fsp-sub fsp-slate-state">{gameState(g.game)}</span>
                </div>
                <div className="fsp-slate-players">
                  <PlayerList label={meName} rows={g.mine} onOpen={onOpen} />
                  <PlayerList
                    label={them.team.abbreviation}
                    rows={g.theirs}
                    onOpen={onOpen}
                    right
                  />
                </div>
              </div>
            ))}
          </section>
        );
      })}
      {idle.length > 0 ? (
        <section className="fsp-slate-window" data-phase="none" aria-label="No game this week">
          <div className="fsp-slate-when">
            <span className="fsp-slate-time">No game this week</span>
          </div>
          <div className="fsp-slate-game">
            <div className="fsp-slate-players">
              <IdleList
                label={meName}
                rows={idle.filter((i) => i.mine).map((i) => i.row)}
                onOpen={onOpen}
              />
              <IdleList
                label={them.team.abbreviation}
                rows={idle.filter((i) => !i.mine).map((i) => i.row)}
                onOpen={onOpen}
                right
              />
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
}

function PlayerList({
  label,
  rows,
  onOpen,
  right = false,
}: {
  /** Whose players, "You" or the team's abbreviation, for a screen reader. */
  label: string;
  rows: MatchupRowView[];
  onOpen: (row: MatchupRowView) => void;
  right?: boolean;
}) {
  const tools = useTools();
  // A projection beside a player still to play is Standard's number.
  const context = useAtLeast('standard');
  return (
    <div className="fsp-slate-side" data-right={right || undefined} role="group" aria-label={label}>
      {rows.map((r, i) => (
        <div key={`${r.slot}-${i}`} className="fsp-slate-player">
          <PositionPill pos={r.slot} size="sm" />
          {/* HIS CLUB AND STATUS UNDER HIS NAME (B-510): beside it, they
              broke a phone's "Ravens" in two. */}
          <span className="fsp-slate-who">
            <button
              type="button"
              className="fsp-linklike fsp-name-btn fsp-slate-name"
              onClick={() => onOpen(r)}
            >
              {r.player ? breakable(r.player.shortName) : 'Empty'}
            </button>
            {r.player ? (
              <span className="fsp-name-marks">
                <PlayerMarks player={r.player} />
              </span>
            ) : null}
          </span>
          <span className="fsp-num fsp-slate-points">
            {r.points !== null ? (
              r.points.toFixed(1)
            ) : r.game?.state === 'pre' ? (
              context && tools.projections ? (
                // THE WORD UNDER THE NUMBER, not beside it: a phone's half of
                // a game had room for "R.Ha…" beside "10.3 proj" (B-295 says
                // a projection is labeled; it does not say where).
                <>
                  <span className="fsp-slate-proj-num">{num(r.projection)}</span>
                  <span className="fsp-slate-proj">proj</span>
                </>
              ) : null
            ) : (
              // Kicked off and his line has not landed: a wait, not a zero (B-172).
              <span className="fsp-muted">—</span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

function IdleList({
  label,
  rows,
  onOpen,
  right = false,
}: {
  label: string;
  rows: MatchupRowView[];
  onOpen: (row: MatchupRowView) => void;
  right?: boolean;
}) {
  return (
    <div className="fsp-slate-side" data-right={right || undefined} role="group" aria-label={label}>
      {rows.map((r, i) => (
        <div key={`${r.slot}-${i}`} className="fsp-slate-player">
          <PositionPill pos={r.slot} size="sm" />
          {r.player ? (
            <span className="fsp-slate-who">
              <button
                type="button"
                className="fsp-linklike fsp-name-btn fsp-slate-name"
                onClick={() => onOpen(r)}
              >
                {breakable(r.player.shortName)}
              </button>
              <ProTeamCode code={r.player.proTeam} />
            </span>
          ) : (
            <span className="fsp-muted fsp-slate-name">Empty</span>
          )}
          <Caption>
            {r.player === null
              ? 'empty'
              : r.game?.state === 'bye' || r.player.onBye
                ? 'bye'
                : 'no game'}
          </Caption>
        </div>
      ))}
    </div>
  );
}
